import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import ConversationList from './components/ConversationList';
import ConversationThread from './components/ConversationThread';
import type { MsgRow, ContactInfo, Conversation } from './types';
import type { MessageAttachment } from '@/lib/messageAttachment';

interface UserRow {
  id: number;
  name: string | null;
  nomcommerce: string | null;
  user_name: string | null;
  image: string | null;
}

/**
 * Boîte de réception du marchand : tous les messages privés échangés avec
 * ses clients, et la possibilité de répondre directement ici.
 * Isolation : on ne charge que les fils où le commerce est expéditeur ou
 * destinataire → chaque commerce ne voit que ses propres conversations.
 */
export default function MerchantMessagesPage() {
  const { user } = useAuth();
  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<MsgRow[]>([]);
  const [contacts, setContacts] = useState<Record<number, ContactInfo>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!commerceId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('id, sender_id, receiver_id, message, created_at, is_read, attachment_url, attachment_name, attachment_type')
        .or(`sender_id.eq.${commerceId},receiver_id.eq.${commerceId}`)
        .order('created_at', { ascending: true })
        .limit(2000);
      if (err) throw err;

      const msgs = (data || []) as MsgRow[];
      setRows(msgs);

      const otherIds = Array.from(new Set(
        msgs
          .map((m) => (m.sender_id === commerceId ? m.receiver_id : m.sender_id))
          .filter((id) => id && id !== commerceId),
      ));

      if (otherIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, nomcommerce, user_name, image')
          .in('id', otherIds);
        const map: Record<number, ContactInfo> = {};
        ((users || []) as UserRow[]).forEach((u) => {
          map[u.id] = {
            id: u.id,
            name: u.nomcommerce || u.name || u.user_name || `Client #${u.id}`,
            user_name: u.user_name,
            image: u.image,
          };
        });
        setContacts(map);
      } else {
        setContacts({});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement des messages');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const conversations = useMemo<Conversation[]>(() => {
    if (!commerceId) return [];
    const map = new Map<number, MsgRow[]>();
    for (const m of rows) {
      const other = m.sender_id === commerceId ? m.receiver_id : m.sender_id;
      if (!other || other === commerceId) continue;
      const bucket = map.get(other);
      if (bucket) bucket.push(m);
      else map.set(other, [m]);
    }
    const list: Conversation[] = [];
    map.forEach((msgs, contactId) => {
      const last = msgs[msgs.length - 1];
      const unread = msgs.filter((m) => m.receiver_id === commerceId && !m.is_read).length;
      list.push({
        contactId,
        contact: contacts[contactId] || null,
        lastMessage: last.message || (last.attachment_name ? `Pièce jointe : ${last.attachment_name}` : ''),
        lastAt: last.created_at,
        unread,
        messages: msgs,
      });
    });
    return list.sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  }, [rows, contacts, commerceId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) => (c.contact?.name || '').toLowerCase().includes(q) || c.lastMessage.toLowerCase().includes(q),
    );
  }, [conversations, search]);

  const selected = useMemo(
    () => conversations.find((c) => c.contactId === selectedId) || null,
    [conversations, selectedId],
  );

  // Marque comme lus les messages entrants de la conversation ouverte.
  useEffect(() => {
    if (!selected || !commerceId) return;
    const unread = selected.messages.filter((m) => m.receiver_id === commerceId && !m.is_read);
    if (unread.length === 0) return;
    const ids = unread.map((m) => m.id);
    void supabase
      .from('messages')
      .update({ is_read: 1 })
      .in('id', ids)
      .then(() => window.dispatchEvent(new Event('messages-changed')));
    setRows((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, is_read: 1 } : m)));

    // Cloche + badge de l'icône : on solde aussi les notifications « message »
    // liées à ce fil, pour que le compteur se mette à jour aussitôt.
    void supabase
      .from('notifications')
      .update({ is_read: 1 })
      .eq('user_id', commerceId)
      .eq('type', 'message')
      .eq('publication_id', selected.contactId)
      .eq('is_read', 0)
      .then(() => window.dispatchEvent(new Event('notifications-changed')));
  }, [selected, commerceId]);

  const sendReply = useCallback(async (text: string, attachment?: MessageAttachment | null): Promise<boolean> => {
    if (!commerceId || !selectedId || sending) return false;
    if (!text.trim() && !attachment) return false;
    setSending(true);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .insert({
          sender_id: commerceId,
          receiver_id: selectedId,
          message: text.trim(),
          is_read: 0,
          attachment_url: attachment?.url ?? null,
          attachment_name: attachment?.name ?? null,
          attachment_type: attachment?.type ?? null,
        })
        .select('id, sender_id, receiver_id, message, created_at, is_read, attachment_url, attachment_name, attachment_type')
        .maybeSingle();
      if (err) throw err;

      const inserted = (data as MsgRow) || {
        id: Date.now(),
        sender_id: commerceId,
        receiver_id: selectedId,
        message: text.trim(),
        created_at: new Date().toISOString(),
        is_read: 0,
        attachment_url: attachment?.url ?? null,
        attachment_name: attachment?.name ?? null,
        attachment_type: attachment?.type ?? null,
      };
      setRows((prev) => [...prev, inserted]);

      // Notification Core best-effort : prévient le client de la réponse.
      try {
        await supabase.from('core_notifications').insert({
          channel: 'in_app',
          type: 'merchant_private_message',
          title: 'Nouveau message de la boutique',
          body: (inserted.message || inserted.attachment_name || 'Pièce jointe').slice(0, 300),
          entity_type: 'client_message',
          entity_id: String(commerceId),
          recipient_user_id: selectedId,
          status: 'pending',
          metadata: { merchant_id: commerceId },
        });
      } catch { /* best-effort */ }

      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }, [commerceId, selectedId, sending]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-chat-3-line text-accent-500"></i>
          Messages clients
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Répondez directement aux messages privés de vos clients, sans quitter votre dashboard.
        </p>
      </div>

      {error && (
        <div className="flex flex-col items-center justify-center py-6 mb-4">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={fetchAll}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        <div className={`${selected ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 flex-shrink-0`}>
          <ConversationList
            conversations={filtered}
            selectedId={selectedId}
            loading={loading}
            search={search}
            onSearch={setSearch}
            onSelect={setSelectedId}
          />
        </div>

        <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
          {selected ? (
            <ConversationThread
              conversation={selected}
              commerceId={commerceId || 0}
              sending={sending}
              onSend={sendReply}
              onBack={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 min-h-[420px] bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
              <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                <i className="ri-chat-3-line text-2xl text-accent-600"></i>
              </div>
              <p className="text-sm font-medium text-foreground-800">Sélectionnez une conversation</p>
              <p className="text-xs text-foreground-500 mt-1">Choisissez un client à gauche pour lire et répondre à ses messages.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}